const COOKIE_CONSENT_NAME = 'CookieConsent';
const COOKIE_CONSENT_PROPAGATED_NAME = 'CookieConsentPopagated';
const PROPAGATION_FLAG = '__cookieConsentPropagationApplied';

const COOKIEBOT_MAX_AGE = 31536000; // 1 rok

const getCookieValue = (doc: Document, name: string): string | undefined => {
  const cookieString = `; ${doc.cookie}`;
  const parts = cookieString.split(`; ${name}=`);

  if (parts.length === 2) {
    return parts.pop()?.split(';').shift();
  }

  return undefined;
};

const buildTopDomain = (host: string | undefined): string | undefined => {
  if (!host) {
    return undefined;
  }

  const segments = host.split('.').filter(Boolean);

  if (!segments.length) {
    return undefined;
  }

  const lastTwoSegments = segments.slice(-2);

  return `.${lastTwoSegments.join('.')}`;
};

const getCookieDescriptor = (doc: Document): PropertyDescriptor | undefined => {
  return (
    Object.getOwnPropertyDescriptor(doc, 'cookie') ??
    Object.getOwnPropertyDescriptor(Document.prototype, 'cookie') ??
    Object.getOwnPropertyDescriptor(HTMLDocument.prototype, 'cookie')
  );
};

const cleanupOriginalCookie = (doc: Document): string | undefined => {
  const cookieConsent = getCookieValue(doc, COOKIE_CONSENT_NAME);
  const propagated = getCookieValue(doc, COOKIE_CONSENT_PROPAGATED_NAME);

  if (cookieConsent != null && propagated == null) {
    doc.cookie = `${COOKIE_CONSENT_NAME}=null; max-age=0; path=/; secure=true`;
  }

  return cookieConsent ?? undefined;
};

const propagateCookie = (doc: Document, domain: string, cookieValue: string): void => {
  doc.cookie = `${COOKIE_CONSENT_NAME}=${cookieValue}; max-age=${COOKIEBOT_MAX_AGE}; path=/; domain=${domain}; secure=true`;
};

const patchDocumentCookie = (doc: Document, descriptor: PropertyDescriptor, domain: string): void => {
  Object.defineProperty(doc, 'cookie', {
    configurable: true,
    enumerable: true,
    get: () => descriptor.get?.call(doc) ?? '',
    set: (value: string) => {
      let cookieValue = value;

      if (value.includes(`${COOKIE_CONSENT_NAME}=`) || value.includes(`${COOKIE_CONSENT_NAME} `)) {
        cookieValue = value
          .split(';')
          .filter((part) => !part.trim().toLowerCase().startsWith('domain'))
          .join('; ');
        cookieValue = `${cookieValue}; domain=${domain}`;
        descriptor.set?.call(
          doc,
          `${COOKIE_CONSENT_PROPAGATED_NAME}=true; max-age=${COOKIEBOT_MAX_AGE}; path=/; secure=true`,
        );
      }

      descriptor.set?.call(doc, cookieValue);
    },
  });
};

export const setupCookieConsentPropagation = (win: Window, doc: Document): void => {
  const runtimeWindow = win as Window & { [PROPAGATION_FLAG]?: boolean };

  if (runtimeWindow[PROPAGATION_FLAG]) {
    return;
  }

  const descriptor = getCookieDescriptor(doc);

  if (!descriptor?.configurable || typeof descriptor.set !== 'function') {
    return;
  }

  const domain = buildTopDomain(win.location?.hostname ?? win.location?.host);

  if (!domain) {
    return;
  }

  const originalValue = cleanupOriginalCookie(doc);

  patchDocumentCookie(doc, descriptor, domain);

  if (originalValue != null) {
    propagateCookie(doc, domain, originalValue);
  }

  runtimeWindow[PROPAGATION_FLAG] = true;
};

declare global {
  interface Window {
    [PROPAGATION_FLAG]?: boolean;
  }
}
